import { Slider } from '@mui/material';
import Box from '@mui/material/Box';
import { memo, useState } from 'react';
import useMoney from '../../../../hooks/useMoney';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import InputHandler from '../../../Crud/filters/components/NumberRangeFilter/components/InputHandler/InputHandler';
import useWidth from '../../../Hooks/useWidth';
import useStyles from './numberRangeFilter.styles';
import { NumberFilterValue, NumberRangeFilterPropsV2 } from './numberRangeFilter.types';
import { formatNumberLabel } from './numberRangeFilter.utils';

const DEFAULT_MAX_VALUE = 1000000;

type ErrorType = 'min' | 'max' | null;

const NumberRangeFilterV2 = ({
  type,
  maxAvailableValue,
  minAvailableValue,
  onChange,
  onValidationError,
  value,
}: NumberRangeFilterPropsV2) => {
  const { formatMoney } = useMoney();
  const { mobileWidth } = useWidth();
  const { translate, locale } = useTranslation();
  const { classes } = useStyles();

  const [draftValue, setDraftValue] = useState<NumberFilterValue>(value);
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [prevValue, setPrevValue] = useState(value);

  if (value.minValue !== prevValue.minValue || value.maxValue !== prevValue.maxValue) {
    setPrevValue(value);
    setDraftValue(value);
    setErrorType(null);
  }

  const minValue = minAvailableValue ?? 0;
  const maxValue = maxAvailableValue || DEFAULT_MAX_VALUE;
  const isDegenerate = maxValue <= minValue;

  const handleInputChange = (key: keyof NumberFilterValue, newValue: number) => {
    const next = { ...draftValue, [key]: newValue };
    setDraftValue(next);
    const isInvalid = next.minValue > next.maxValue;
    onValidationError?.(isInvalid);
    if (isInvalid) {
      setErrorType(key === 'minValue' ? 'min' : 'max');
      return;
    }
    setErrorType(null);
    onChange(next);
  };

  const handleSliderChange = (values: number[]) => {
    const next = { minValue: values[0], maxValue: values[1] };
    setDraftValue(next);
    setErrorType(null);
    onValidationError?.(false);
    onChange(next);
  };

  const inputsArgs = [
    {
      key: 'min' as const,
      onChange: (newMin: number) => handleInputChange('minValue', newMin),
      value: draftValue.minValue,
      errorMessage:
        errorType === 'min'
          ? translate(LangEnum.FILTER_NUMBER_RANGE_MIN_GREATER_THAN_MAX)
          : undefined,
    },
    {
      key: 'max' as const,
      onChange: (newMax: number) => handleInputChange('maxValue', newMax),
      value: draftValue.maxValue,
      errorMessage:
        errorType === 'max' ? translate(LangEnum.FILTER_NUMBER_RANGE_MAX_LESS_THAN_MIN) : undefined,
    },
  ];

  return (
    <Box
      sx={{
        maxHeight: mobileWidth ? '100%' : '284px',
        flexGrow: mobileWidth ? 1 : 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        paddingX: 1,
        paddingTop: 1,
        overflowX: 'hidden',
        paddingBottom: 1,
      }}
    >
      <Box
        role="group"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {inputsArgs.map((args) => (
          <InputHandler
            key={args.key}
            type={type}
            value={args.value}
            onChange={args.onChange}
            error={!!args.errorMessage}
            helperText={args.errorMessage}
          />
        ))}
      </Box>
      {!isDegenerate && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingX: 1.25,
          }}
        >
          <Slider
            size="small"
            className={classes.slider}
            min={minValue}
            max={maxValue}
            value={[draftValue.minValue, draftValue.maxValue]}
            valueLabelFormat={(value) => formatNumberLabel(type, value, formatMoney, locale)}
            marks={[
              { value: minValue, label: translate(LangEnum.GLOBAL_MINIMUM) },
              {
                value: maxValue,
                label: translate(LangEnum.GLOBAL_MAXIMUM),
              },
            ]}
            onChange={(_, values) => {
              if (Array.isArray(values)) {
                handleSliderChange(values);
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(NumberRangeFilterV2);
